import { useState, useEffect, useRef, useMemo } from "react";
import moment from "moment-timezone";
import { Trash2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";

import { useListCustomers } from "@/lib/api/customer";
import {
  useCreatePaymentRemittanceBatch,
  useFinalizePaymentRemittanceBatch,
  type PaymentRemittanceCreatedUpload,
} from "@/lib/api/paymentRemittancePdfs";
import { UploadPresignedFile } from "@/lib/file";

interface CreateBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

type Stage =
  | { kind: "idle" }
  | { kind: "creating" }
  | { kind: "uploading"; total: number; done: number }
  | { kind: "finalizing" }
  | { kind: "done" }
  | { kind: "error"; message: string };

/**
 * Drives the three-step batch-create flow:
 *   1. POST create → returns one presigned PUT URL per file
 *   2. PUT each file directly to S3 (parallel)
 *   3. POST finalize → enqueues the grandparent orchestration job
 *
 * Each step's progress is shown inline so the operator can see where
 * a hang/failure happens. On any failure after step 1 commits, the
 * batch row already exists in `awaiting_uploads` — re-running this
 * dialog from scratch creates a *new* batch (no resume in v1).
 */
export function CreateBatchDialog({
  open,
  onOpenChange,
  onComplete,
}: CreateBatchDialogProps) {
  const { toast } = useToast();

  const [customerId, setCustomerId] = useState<string>("");
  const [periodMonth, setPeriodMonth] = useState<string>(() =>
    moment.utc().format("YYYY-MM"),
  );
  const [files, setFiles] = useState<File[]>([]);
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const customersResult = useListCustomers({ minimal: true });
  const customerOptions = useMemo(
    () =>
      (customersResult?.data?.data || []).map(
        (c: { id: string; short_name: string; long_name: string }) => ({
          value: c.id,
          label: c.long_name ?? "",
        }),
      ),
    [customersResult],
  );

  // Reset internal state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setCustomerId("");
      setPeriodMonth(moment.utc().format("YYYY-MM"));
      setFiles([]);
      setStage({ kind: "idle" });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const createBatch = useCreatePaymentRemittanceBatch();
  const finalizeBatch = useFinalizePaymentRemittanceBatch();

  const handleFilesChosen = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const onlyPdfs = incoming.filter((f: File) =>
      f.name.toLowerCase().endsWith(".pdf"),
    );
    if (onlyPdfs.length !== incoming.length) {
      toast({
        title: "Non-PDF files were skipped",
        description: "Only .pdf files are accepted.",
      });
    }
    setFiles((prev: File[]) => [...prev, ...onlyPdfs]);
  };

  const handleRemoveFile = (idx: number) => {
    setFiles((prev: File[]) => prev.filter((_: File, i: number) => i !== idx));
  };

  const isWorking =
    stage.kind === "creating" ||
    stage.kind === "uploading" ||
    stage.kind === "finalizing";

  const handleSubmit = async () => {
    if (!customerId) {
      toast({
        title: "No customer selected",
        variant: "destructive",
      });
      return;
    }
    if (files.length === 0) {
      toast({
        title: "No files selected",
        variant: "destructive",
      });
      return;
    }
    // YYYY-MM → YYYY-MM-01 (spec: first day of month)
    const periodDate = moment.utc(periodMonth, "YYYY-MM", true);
    if (!periodDate.isValid()) {
      toast({
        title: "Invalid period month",
        description: "Expected YYYY-MM",
        variant: "destructive",
      });
      return;
    }
    const periodDateStr = periodDate.startOf("month").format("YYYY-MM-DD");

    // ---- Step 1: create ----
    setStage({ kind: "creating" });
    const createResult = await createBatch({
      customer_id: customerId,
      period_date: periodDateStr,
      files: files.map((f: File) => ({
        filename: f.name,
        content_type: f.type || "application/pdf",
        size_bytes: f.size,
      })),
    });

    if (!createResult.success || !createResult.data) {
      const msg = createResult.message || "Failed to create batch";
      setStage({ kind: "error", message: msg });
      toast({ title: "Create failed", description: msg, variant: "destructive" });
      return;
    }

    const { batch_id, uploads } = createResult.data;

    // ---- Step 2: upload PDFs to S3 ----
    setStage({ kind: "uploading", total: uploads.length, done: 0 });

    let doneCount = 0;
    const uploadResults = await Promise.all(
      uploads.map(async (u: PaymentRemittanceCreatedUpload) => {
        const file = files.find((f: File) => f.name === u.original_filename);
        if (!file) {
          return {
            ok: false,
            filename: u.original_filename,
            message: "Local file not found by name",
          };
        }
        const res = await UploadPresignedFile(
          u.presigned_put_url,
          file,
          file.type || "application/pdf",
        );
        doneCount += 1;
        setStage({ kind: "uploading", total: uploads.length, done: doneCount });
        if (!res.success) {
          return {
            ok: false,
            filename: u.original_filename,
            message: `S3 PUT failed (status ${(res.data as { status?: number }).status ?? "?"})`,
          };
        }
        return { ok: true, filename: u.original_filename, message: "" };
      }),
    );

    const failedUploads = uploadResults.filter((r) => !r.ok);
    if (failedUploads.length > 0) {
      const msg = `${failedUploads.length} of ${uploads.length} S3 uploads failed`;
      setStage({ kind: "error", message: msg });
      toast({
        title: "Upload failed",
        description: `${msg}. The batch row exists but cannot be finalized.`,
        variant: "destructive",
      });
      return;
    }

    // ---- Step 3: finalize ----
    setStage({ kind: "finalizing" });
    const finalizeResult = await finalizeBatch(batch_id);

    if (!finalizeResult.success || !finalizeResult.data) {
      const msg = finalizeResult.message || "Failed to finalize batch";
      setStage({ kind: "error", message: msg });
      toast({
        title: "Finalize failed",
        description: msg,
        variant: "destructive",
      });
      return;
    }

    setStage({ kind: "done" });
    toast({
      title: "Batch finalized",
      description: `Process job id: ${finalizeResult.data.process_job_id}`,
    });
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Create payment remittance batch</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground">
              Customer
            </label>
            <Combobox
              options={customerOptions}
              value={customerId}
              onValueChange={(v: string) => setCustomerId(v)}
              placeholder="Select customer..."
              virtualizeList
              dwimOnChange
              isModal
              disabled={isWorking}
              popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground">
              Period (YYYY-MM)
            </label>
            <Input
              type="month"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              disabled={isWorking}
            />
            <span className="text-xs text-muted-foreground">
              Stored as the first day of the selected month.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-muted-foreground">
              PDFs
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={(e) => handleFilesChosen(e.target.files)}
              disabled={isWorking}
              className="text-sm"
            />
            {files.length > 0 && (
              <ul className="border rounded p-2 text-sm space-y-1">
                {files.map((f: File, idx: number) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex flex-row items-center justify-between gap-2"
                  >
                    <span className="font-mono text-xs break-all">
                      {f.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(idx)}
                      disabled={isWorking}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <StageIndicator stage={stage} />

          <div className="flex flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isWorking}
            >
              {stage.kind === "done" ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isWorking || stage.kind === "done"}
            >
              {stage.kind === "creating" && "Creating..."}
              {stage.kind === "uploading" &&
                `Uploading ${stage.done}/${stage.total}...`}
              {stage.kind === "finalizing" && "Finalizing..."}
              {(stage.kind === "idle" || stage.kind === "error") && "Submit"}
              {stage.kind === "done" && "Done"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StageIndicator({ stage }: { stage: Stage }) {
  if (stage.kind === "idle") return null;
  if (stage.kind === "error") {
    return (
      <div className="rounded border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
        {stage.message}
      </div>
    );
  }
  if (stage.kind === "done") {
    return (
      <div className="rounded border border-green-600 bg-green-50 p-2 text-sm text-green-800">
        Batch finalized — orchestration enqueued.
      </div>
    );
  }
  let label = "";
  if (stage.kind === "creating") label = "Creating batch…";
  if (stage.kind === "uploading")
    label = `Uploading PDFs to S3 (${stage.done}/${stage.total})…`;
  if (stage.kind === "finalizing") label = "Finalizing…";
  return (
    <div className="rounded border bg-muted/40 p-2 text-sm">{label}</div>
  );
}
